// src/Components/Broadcast/BroadcastPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BarChart, Clock, RefreshCw, Gift } from 'lucide-react';
import { collection, getDocs, query, where, limit, collectionGroup } from "firebase/firestore";
import { db } from '../../firebase/firestore';
import { useBroadcast } from './hooks/useBroadcast';
import { useUserFilter } from './hooks/useUserFilter';
import { useProducts } from './hooks/useProducts';
import { Notification } from './components/Notification';
import { StatsPanel } from './components/StatsPanel';
import { HistoryPanel } from './components/HistoryPanel';
import { MessageEditor } from './components/MessageEditor';
import { FilterPanel } from './components/FilterPanel';
import { UserSelector } from './components/UserSelector';
import { PreviewModal } from './components/PreviewModal';
import { GiftBroadcastPanel } from './components/GiftBroadcastPanel';
import { usersCache, productsCache, giftsCache } from './services/cacheService';
import axios from 'axios'; // ДОБАВИТЬ

const API_URL = process.env.REACT_APP_API_URL || 'https://artdownload2-production.up.railway.app';

const BroadcastPage = () => {
  const [showStats, setShowStats] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [showGiftPanel, setShowGiftPanel] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState({ type: '', message: '' });
  const [userList, setUserList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [finalFilteredUsers, setFinalFilteredUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, premium: 0, free: 0, withPhone: 0, withEmail: 0 });
  const [broadcastHistory, setBroadcastHistory] = useState([]);
  const [combinationType, setCombinationType] = useState('none');
  const [excludeSentUsers, setExcludeSentUsers] = useState(false);
  const [sentUserIds, setSentUserIds] = useState([]);
  
  // СОСТОЯНИЯ ДЛЯ ФИЛЬТРАЦИИ ПО ИСТОРИИ
  const [excludeByHistory, setExcludeByHistory] = useState(false);
  const [selectedHistoryItems, setSelectedHistoryItems] = useState([]);
  
  // НОВЫЕ СОСТОЯНИЯ ДЛЯ ФИЛЬТРАЦИИ ПО ОШИБКАМ
  const [excludeSuccessUsers, setExcludeSuccessUsers] = useState(false);
  const [excludeFailedUsers, setExcludeFailedUsers] = useState(false);
  const [failedUsersOnly, setFailedUsersOnly] = useState(false);
  
  // НОВОЕ СОСТОЯНИЕ ДЛЯ ПОДАРКОВ ИЗ API
  const [giftsFromApi, setGiftsFromApi] = useState([]);
  const [loadingGifts, setLoadingGifts] = useState(false);
  
  const usersLoadedRef = useRef(false);
  const historyLoadedRef = useRef(false);
  const abortControllerRef = useRef(null);

  const {
    targetFilter,
    selectedUsers,
    setSelectedUsers,
    filteredUsers,
    handleFilterChange,
    toggleUserSelection,
    selectAllFiltered,
    clearSelection,
    getFilteredUsersBySearch,
    updateFilteredUsersWithFilters
  } = useUserFilter(userList, (type, message) => setNotification({ type, message }));

  const {
    products,
    productFilter,
    setProductFilter,
    gifts,
    giftFilter,
    setGiftFilter,
    excludedUsers,
    excludedGroups,
    excludedProducts,
    addExcludedUser,
    removeExcludedUser,
    addExcludedGroup,
    removeExcludedGroup,
    addExcludedProduct,
    removeExcludedProduct,
    loadUserAccess
  } = useProducts((type, message) => setNotification({ type, message }));

  const {
    messageText,
    setMessageText,
    messageType,
    setMessageType,
    parseMode,
    setParseMode,
    imagePreview,
    sending,
    sendProgress,
    previewData,
    showPreview,
    setShowPreview,
    handleImageSelect,
    handleImageRemove,
    insertFormatting,
    preparePreview,
    sendMessage
  } = useBroadcast({
    targetFilter,
    selectedUsers,
    filteredUsers: finalFilteredUsers,
    userList,
    productFilter,
    giftFilter,
    excludedUsers,
    excludedGroups,
    excludedProducts,
    combinationType,
    excludeSentUsers,
    sentUserIds,
    setNotification: (type, message) => setNotification({ type, message }),
    onHistoryUpdate: setBroadcastHistory
  });

  // ============================================
  // НОВАЯ ФУНКЦИЯ: ЗАГРУЗКА ПОДАРКОВ ИЗ API
  // ============================================
  const loadGiftsFromApi = useCallback(async () => {
    try {
      setLoadingGifts(true);
      console.log('🔄 Загрузка подарков из API...');
      
      const response = await axios.get(`${API_URL}/api/gift-broadcast/gifts`);
      
      console.log('📥 Ответ API:', response.data);
      
      if (response.data.success) {
        const gifts = response.data.gifts || [];
        setGiftsFromApi(gifts);
        console.log(`✅ Загружено ${gifts.length} подарков из API`);
        console.log('📋 Список подарков:', gifts.map(g => ({ code: g.code, title: g.title })));
      } else {
        console.error('❌ Ошибка загрузки подарков:', response.data.error);
      }
    } catch (error) {
      console.error('❌ Ошибка при загрузке подарков:', error);
    } finally {
      setLoadingGifts(false);
    }
  }, []);

  // Функция для получения пользователей с ошибками из истории
  const getFailedUsersFromHistory = useCallback((selectedItems) => {
    const failedUsersSet = new Set();
    const itemsToProcess = selectedItems.length > 0 
      ? broadcastHistory.filter(item => selectedItems.includes(item.id))
      : broadcastHistory;
    
    itemsToProcess.forEach(item => {
      if (item.failedUsers && item.failedUsers.length > 0) {
        item.failedUsers.forEach(fail => {
          failedUsersSet.add(fail.id);
        });
      }
    });
    
    return Array.from(failedUsersSet);
  }, [broadcastHistory]);

  // Функция для получения успешных пользователей из истории
  const getSuccessUsersFromHistory = useCallback((selectedItems) => {
    const successUsersSet = new Set();
    const itemsToProcess = selectedItems.length > 0 
      ? broadcastHistory.filter(item => selectedItems.includes(item.id))
      : broadcastHistory;
    
    itemsToProcess.forEach(item => {
      if (item.successUsers && item.successUsers.length > 0) {
        item.successUsers.forEach(userId => {
          successUsersSet.add(userId);
        });
      }
    });
    
    return Array.from(successUsersSet);
  }, [broadcastHistory]);

  // ОБНОВЛЕННАЯ функция применения фильтров с учетом всех параметров
  const applyAllFilters = useCallback(() => {
    if (userList.length > 0) {
      // Получаем пользователей для исключения из истории
      let historyExcludedUsers = [];
      let forcedUsers = null;
      
      if (selectedHistoryItems.length > 0) {
        if (excludeByHistory || excludeSuccessUsers || excludeFailedUsers || failedUsersOnly) {
          const successUsers = getSuccessUsersFromHistory(selectedHistoryItems);
          const failedUsers = getFailedUsersFromHistory(selectedHistoryItems);
          
          if (failedUsersOnly) {
            // Режим "только получатели с ошибками" - отправляем только тем, у кого были ошибки
            forcedUsers = failedUsers;
          } else {
            // Обычная фильтрация: исключаем выбранные категории
            if (excludeSuccessUsers) {
              historyExcludedUsers.push(...successUsers);
            }
            if (excludeFailedUsers) {
              historyExcludedUsers.push(...failedUsers);
            }
          }
        }
      }
      
      const additionalFilters = {
        productFilter,
        giftFilter,
        excludedUsers: [...excludedUsers, ...historyExcludedUsers],
        excludedGroups,
        excludedProducts,
        combinationType,
        excludeSentUsers,
        sentUserIds,
        // Передаем принудительный список пользователей для режима failedUsersOnly
        forcedUsers
      };
      
      const filtered = updateFilteredUsersWithFilters(additionalFilters);
      setFinalFilteredUsers(filtered);
    }
  }, [userList, updateFilteredUsersWithFilters, productFilter, giftFilter, excludedUsers, excludedGroups, excludedProducts, combinationType, excludeSentUsers, sentUserIds, selectedHistoryItems, excludeByHistory, excludeSuccessUsers, excludeFailedUsers, failedUsersOnly, getSuccessUsersFromHistory, getFailedUsersFromHistory]);

  // Обработчик повторной отправки ошибочным получателям
  const handleResendToFailed = useCallback((failedUserIds) => {
    // Очищаем предыдущие фильтры по истории
    setExcludeByHistory(false);
    setExcludeSuccessUsers(false);
    setExcludeFailedUsers(false);
    // Включаем режим "только получатели с ошибками"
    setFailedUsersOnly(true);
    // Выбираем все рассылки, из которых берем ошибочных пользователей
    setSelectedHistoryItems(broadcastHistory.map(item => item.id));
    // Устанавливаем выбранных пользователей
    setSelectedUsers(failedUserIds);
    handleFilterChange('selected');
    setNotification({ type: 'info', message: `Выбрано ${failedUserIds.length} пользователей для повторной отправки` });
    // Применяем фильтры
    setTimeout(() => applyAllFilters(), 100);
  }, [broadcastHistory, setSelectedUsers, handleFilterChange, applyAllFilters]);

  // Обработчик повторной отправки для выбранных рассылок
  const handleResendSelectedToFailed = useCallback((failedUserIds) => {
    if (failedUserIds && failedUserIds.length > 0) {
      // Очищаем предыдущие фильтры по истории
      setExcludeByHistory(false);
      setExcludeSuccessUsers(false);
      setExcludeFailedUsers(false);
      // Включаем режим "только получатели с ошибками"
      setFailedUsersOnly(true);
      // Устанавливаем выбранных пользователей
      setSelectedUsers(failedUserIds);
      handleFilterChange('selected');
      setNotification({ type: 'info', message: `Выбрано ${failedUserIds.length} пользователей для повторной отправки из выбранных рассылок` });
      // Применяем фильтры
      setTimeout(() => applyAllFilters(), 100);
    }
  }, [setSelectedUsers, handleFilterChange, applyAllFilters]);

  // Обработчик исключения успешно получивших пользователей
  const handleExcludeSentUsers = useCallback((successUserIds) => {
    setSentUserIds(prev => [...new Set([...prev, ...successUserIds])]);
    setExcludeSentUsers(true);
    setTimeout(() => applyAllFilters(), 100);
    setNotification({ type: 'info', message: `Исключено ${successUserIds.length} пользователей, уже получивших рассылку` });
  }, [applyAllFilters]);

  // Обработчик изменения настроек истории из FilterPanel
  const handleHistoryFilterChange = useCallback((exclude, selectedItems) => {
    setExcludeByHistory(exclude);
    setSelectedHistoryItems(selectedItems);
    // Применяем фильтры после изменения
    setTimeout(() => applyAllFilters(), 100);
  }, [applyAllFilters]);

  // Оптимизированная загрузка подарков для пользователей
  const loadUserGifts = useCallback(async (userIds) => {
    const giftsMap = new Map();
    
    // Инициализируем пустыми массивами
    userIds.forEach(id => giftsMap.set(id, []));
    
    try {
      // Пытаемся использовать collectionGroup
      if (typeof collectionGroup === 'function') {
        const giftsGroupRef = collectionGroup(db, "gifts");
        const giftsSnapshot = await getDocs(giftsGroupRef);
        
        giftsSnapshot.docs.forEach(giftDoc => {
          // Путь к документу: telegramUsers/{userId}/gifts/{giftId}
          const pathParts = giftDoc.ref.path.split('/');
          const userId = pathParts[1]; // telegramUsers/{userId}/gifts/...
          
          if (userId && giftsMap.has(userId)) {
            giftsMap.get(userId).push({
              id: giftDoc.id,
              ...giftDoc.data()
            });
          }
        });
      } else {
        console.warn('collectionGroup not supported, using fallback');
        // Fallback: загружаем подарки небольшими пачками
        const batchSize = 10;
        for (let i = 0; i < userIds.length; i += batchSize) {
          const batch = userIds.slice(i, i + batchSize);
          await Promise.all(batch.map(async (userId) => {
            try {
              const giftsRef = collection(db, "telegramUsers", userId, "gifts");
              const giftsSnapshot = await getDocs(giftsRef);
              giftsMap.set(userId, giftsSnapshot.docs.map(giftDoc => ({
                id: giftDoc.id,
                ...giftDoc.data()
              })));
            } catch (err) {
              console.error(`Error loading gifts for user ${userId}:`, err);
            }
          }));
        }
      }
    } catch (error) {
      console.error('Error loading gifts:', error);
    }
    
    return giftsMap;
  }, []);

  // Оптимизированная загрузка пользователей
  const fetchUsers = useCallback(async () => {
    if (usersLoadedRef.current || loadingUsers) return;
    
    // Проверяем кэш
    const cachedUsers = usersCache.get();
    if (cachedUsers) {
      console.log('Using cached users:', cachedUsers.length);
      setUserList(cachedUsers);
      usersLoadedRef.current = true;
      
      // Обновляем статистику
      const total = cachedUsers.length;
      const premium = cachedUsers.filter(u => u.premium).length;
      const free = total - premium;
      const withPhone = cachedUsers.filter(u => u.phone).length;
      const withEmail = cachedUsers.filter(u => u.email).length;
      setStats({ total, premium, free, withPhone, withEmail });
      
      await loadUserAccess(cachedUsers);
      setTimeout(() => applyAllFilters(), 100);
      return;
    }
    
    // Отменяем предыдущий запрос, если есть
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    setLoadingUsers(true);
    try {
      // 1. Загружаем всех пользователей одним запросом
      const usersRef = collection(db, "telegramUsers");
      const usersSnapshot = await getDocs(usersRef);
      
      // 2. Собираем ID всех пользователей
      const userIds = usersSnapshot.docs.map(doc => doc.id);
      
      // 3. Загружаем подарки для всех пользователей
      const giftsMap = await loadUserGifts(userIds);
      
      // 4. Формируем список пользователей с подарками
      const users = usersSnapshot.docs.map(doc => {
        const data = doc.data();
        const userGifts = giftsMap.get(doc.id) || [];
        
        return {
          id: doc.id,
          username: data.username || null,
          fullName: data.fullName || null,
          phone: data.phone || null,
          email: data.email || null,
          premium: data.premium || false,
          premiumActivationType: data.premiumActivationType || null,
          joinedAt: data.joinedAt || new Date().toISOString(),
          courses: data.courses || [],
          coursesAccess: data.coursesAccess || [],
          trainings: data.trainings || [],
          trainingsAccess: data.trainingsAccess || [],
          gifts: userGifts,
          giftIds: userGifts.map(g => g.id)
        };
      });

      // Сохраняем в кэш
      usersCache.set(users);
      setUserList(users);
      usersLoadedRef.current = true;
      
      // Обновляем статистику
      const total = users.length;
      const premium = users.filter(u => u.premium).length;
      const free = total - premium;
      const withPhone = users.filter(u => u.phone).length;
      const withEmail = users.filter(u => u.email).length;
      setStats({ total, premium, free, withPhone, withEmail });
      
      await loadUserAccess(users);
      setTimeout(() => applyAllFilters(), 100);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Ошибка загрузки пользователей:", error);
        setNotification({ type: 'error', message: `Ошибка загрузки: ${error.message}` });
      }
    } finally {
      setLoadingUsers(false);
      abortControllerRef.current = null;
    }
  }, [loadingUsers, loadUserAccess, applyAllFilters, loadUserGifts]);

  // Загружаем подарки из API при первом рендере
  useEffect(() => {
    loadGiftsFromApi();
  }, [loadGiftsFromApi]);

  // Оптимизированная загрузка истории (с ограничением)
  const loadHistory = useCallback(() => {
    if (historyLoadedRef.current) return;
    try {
      const history = JSON.parse(localStorage.getItem('broadcastHistory') || '[]');
      // Ограничиваем историю последними 20 записями для экономии памяти
      const limitedHistory = history.slice(0, 20);
      setBroadcastHistory(limitedHistory);
      historyLoadedRef.current = true;
    } catch (e) {}
  }, []);

  const clearHistory = useCallback(() => {
    if (window.confirm('Очистить всю историю рассылок?')) {
      localStorage.removeItem('broadcastHistory');
      setBroadcastHistory([]);
      setNotification({ type: 'success', message: 'Вся история очищена' });
    }
  }, []);

  const handleDeleteHistoryItem = useCallback((itemId) => {
    if (window.confirm('Удалить эту запись из истории?')) {
      const updatedHistory = broadcastHistory.filter(item => item.id !== itemId);
      setBroadcastHistory(updatedHistory);
      localStorage.setItem('broadcastHistory', JSON.stringify(updatedHistory));
      setNotification({ type: 'success', message: 'Запись удалена' });
    }
  }, [broadcastHistory]);

  const handleOpenGiftPanel = useCallback(() => {
    setShowGiftPanel(true);
  }, []);

  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }, []);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    fetchUsers();
    loadHistory();
  }, [fetchUsers, loadHistory]);

  useEffect(() => {
    if (userList.length > 0) {
      applyAllFilters();
    }
  }, [userList, productFilter, giftFilter, excludedUsers, excludedGroups, excludedProducts, combinationType, excludeSentUsers, sentUserIds, selectedHistoryItems, excludeByHistory, excludeSuccessUsers, excludeFailedUsers, failedUsersOnly, targetFilter, selectedUsers, applyAllFilters]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 overflow-hidden">
      <div className="h-screen flex flex-col">
        <Notification notification={notification} />

        <div className="flex-shrink-0 bg-gray-900/50 backdrop-blur-sm border-b border-gray-700">
          <div className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => window.history.back()} 
                  className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors border border-gray-700"
                  title="Назад"
                >
                  <span className="text-gray-300 text-xl">←</span>
                </button>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-white">Рассылка сообщений</h1>
                  <p className="text-gray-400 mt-1">Отправка уведомлений через Telegram</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setShowStats(!showStats)} 
                  className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors border border-gray-700"
                  title="Статистика"
                >
                  <BarChart className="w-5 h-5 text-gray-300" />
                </button>
                <button 
                  onClick={() => setShowHistory(!showHistory)} 
                  className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors border border-gray-700"
                  title="История рассылок"
                >
                  <Clock className="w-5 h-5 text-gray-300" />
                </button>
                <button 
                  onClick={handleOpenGiftPanel} 
                  className="p-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition-colors border border-purple-500"
                  title="Управление рассылками подарков"
                >
                  <Gift className="w-5 h-5 text-white" />
                </button>
                <button 
                  onClick={fetchUsers} 
                  disabled={loadingUsers} 
                  className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors border border-gray-700 disabled:opacity-50"
                  title="Обновить список пользователей"
                >
                  <RefreshCw className={`w-5 h-5 text-gray-300 ${loadingUsers ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            <StatsPanel showStats={showStats} stats={stats} />
            <HistoryPanel
              showHistory={showHistory}
              broadcastHistory={broadcastHistory}
              formatDate={formatDate}
              clearHistory={clearHistory}
              onResendToFailed={handleResendToFailed}
              onExcludeSentUsers={handleExcludeSentUsers}
              onDeleteItem={handleDeleteHistoryItem}
              onResendSelectedToFailed={handleResendSelectedToFailed}
            />
          </div>
        </div>

        <div className="flex-1 overflow-hidden px-4 md:px-6 pb-4">
          <div className="h-full overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-full">
              <div className="lg:col-span-2 space-y-4">
                <MessageEditor
                  messageText={messageText}
                  setMessageText={setMessageText}
                  messageType={messageType}
                  setMessageType={setMessageType}
                  parseMode={parseMode}
                  setParseMode={setParseMode}
                  imagePreview={imagePreview}
                  handleImageSelect={handleImageSelect}
                  handleImageRemove={handleImageRemove}
                  insertFormatting={insertFormatting}
                  preparePreview={preparePreview}
                  setShowUserSelector={setShowUserSelector}
                  sending={sending}
                />
              </div>

              <div className="space-y-4">
                <FilterPanel
                  targetFilter={targetFilter}
                  handleFilterChange={handleFilterChange}
                  stats={stats}
                  selectedUsers={selectedUsers}
                  filteredUsers={finalFilteredUsers}
                  products={products}
                  productFilter={productFilter}
                  setProductFilter={setProductFilter}
                  gifts={giftsFromApi} // ИЗМЕНЕНО: используем подарки из API вместо gifts из useProducts
                  giftFilter={giftFilter}
                  setGiftFilter={setGiftFilter}
                  excludedUsers={excludedUsers}
                  excludedGroups={excludedGroups}
                  excludedProducts={excludedProducts}
                  combinationType={combinationType}
                  setCombinationType={setCombinationType}
                  excludeSentUsers={excludeSentUsers}
                  setExcludeSentUsers={setExcludeSentUsers}
                  addExcludedUser={addExcludedUser}
                  removeExcludedUser={removeExcludedUser}
                  addExcludedGroup={addExcludedGroup}
                  removeExcludedGroup={removeExcludedGroup}
                  addExcludedProduct={addExcludedProduct}
                  removeExcludedProduct={removeExcludedProduct}
                  onApplyFilters={applyAllFilters}
                  broadcastHistory={broadcastHistory}
                  excludeByHistory={excludeByHistory}
                  setExcludeByHistory={setExcludeByHistory}
                  selectedHistoryItems={selectedHistoryItems}
                  setSelectedHistoryItems={setSelectedHistoryItems}
                  excludeSuccessUsers={excludeSuccessUsers}
                  setExcludeSuccessUsers={setExcludeSuccessUsers}
                  excludeFailedUsers={excludeFailedUsers}
                  setExcludeFailedUsers={setExcludeFailedUsers}
                  failedUsersOnly={failedUsersOnly}
                  setFailedUsersOnly={setFailedUsersOnly}
                  onResendToFailed={handleResendToFailed}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <UserSelector
        showUserSelector={showUserSelector}
        setShowUserSelector={setShowUserSelector}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedUsers={selectedUsers}
        allUsers={userList}
        filteredUsers={finalFilteredUsers}
        toggleUserSelection={toggleUserSelection}
        selectAllFiltered={() => {
          const filtered = getFilteredUsersBySearch(searchTerm, finalFilteredUsers);
          selectAllFiltered(filtered);
          setTimeout(applyAllFilters, 100);
        }}
        clearSelection={() => {
          clearSelection();
          setTimeout(applyAllFilters, 100);
        }}
        getFilteredUsersBySearch={(term) => getFilteredUsersBySearch(term, finalFilteredUsers)}
        handleFilterChange={(filter) => {
          handleFilterChange(filter);
          setTimeout(applyAllFilters, 100);
        }}
        showNotification={(type, message) => setNotification({ type, message })}
      />

      <PreviewModal
        showPreview={showPreview}
        setShowPreview={setShowPreview}
        previewData={previewData}
        targetFilter={targetFilter}
        sending={sending}
        sendProgress={sendProgress}
        sendMessage={sendMessage}
      />

      <GiftBroadcastPanel
        showGiftPanel={showGiftPanel}
        onClose={() => setShowGiftPanel(false)}
      />
    </div>
  );
};

export default BroadcastPage;